import { Stack, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { FontFamily } from "../../../enums";
import mathJaxRender from "../../../utility/mathJaxRender";
import QuestionOptionPreview from "./QuestionOptionPreview";

interface QuestionPreviewItemProps {
  questionData: {
    questionId: number;
    question: string;
    questionHindi: string;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    option1Hindi: string;
    option2Hindi: string;
    option3Hindi: string;
    option4Hindi: string;
    explanation: string;
    explanationHindi: string;
    correctAnswerEnglish: string;
    correctAnswerHindi: string | null;
  };
  language: "english" | "hindi";
}

const QuestionPreviewItem: React.FC<QuestionPreviewItemProps> = ({
  questionData,
  language,
}) => {
  useEffect(() => {
    mathJaxRender();
  }, [questionData, language]);

  const questionText = language === "hindi" 
    ? questionData.questionHindi 
    : questionData.question;

  const correctAnswer = language === "hindi" 
    ? questionData.correctAnswerHindi 
    : questionData.correctAnswerEnglish;

  const options = [
    {
      text: language === "hindi" ? questionData.option1Hindi : questionData.option1,
      index: 0,
      isCorrect: correctAnswer === (language === "hindi" ? questionData.option1Hindi : questionData.option1)
    },
    {
      text: language === "hindi" ? questionData.option2Hindi : questionData.option2,
      index: 1,
      isCorrect: correctAnswer === (language === "hindi" ? questionData.option2Hindi : questionData.option2)
    },
    {
      text: language === "hindi" ? questionData.option3Hindi : questionData.option3,
      index: 2,
      isCorrect: correctAnswer === (language === "hindi" ? questionData.option3Hindi : questionData.option3)
    },
    {
      text: language === "hindi" ? questionData.option4Hindi : questionData.option4,
      index: 3,
      isCorrect: correctAnswer === (language === "hindi" ? questionData.option4Hindi : questionData.option4)
    }
  ];

  const explanationText = language === "hindi" 
    ? questionData.explanationHindi 
    : questionData.explanation;

  return (
    <Stack sx={{ 
      width: "375px",
      minWidth: "375px",
      height: "calc(100% - 48px)",
      margin: "24px 0",
      padding: "16px", 
      border: "1px solid #E0E0E0", 
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      bgcolor: "#FFFFFF",
      overflow: "auto"
    }}>
      {/* Language Label */}
      <Typography
        color={"#666"}
        fontFamily={FontFamily.gilroy}
        fontSize={"14px"}
        fontWeight={600}
        mb={"12px"}
      >
        {language === "hindi" ? "Hindi" : "English"}
      </Typography>

      {/* Question Text */}
      {questionText ? (
        <Typography
          component={"div"}
          color={"#121212"}
          fontWeight={400}
          fontSize={"16px"}
          lineHeight={"125%"}
          fontFamily={FontFamily.gilroy}
          mb={"16px"}
        >
          <div
            style={{ wordBreak: "break-word", whiteSpace: "normal" }}
            dangerouslySetInnerHTML={{
              __html: questionText || "",
            }}
          />
        </Typography>
      ) : (
        <Stack
          p={"12px"}
          border={"1px solid #FFB74D"}
          borderRadius={"5px"}
          bgcolor={"#FFF3E0"}
          mb={"16px"}
        >
          <Typography
            color={"#F57C00"}
            fontWeight={400}
            fontSize={"14px"}
            fontFamily={FontFamily.gilroy}
            fontStyle={"italic"}
          >
            No {language === "hindi" ? "Hindi" : "English"} translation available
          </Typography>
        </Stack>
      )}

      {/* Question Options */}
      <Stack gap={"8px"} mb={"16px"}>
        {options.map((option) => {
          if (!option.text) {
            return (
              <Stack
                key={option.index}
                p={"12px"}
                border={"1px solid #FFB74D"}
                borderRadius={"5px"}
                bgcolor={"#FFF3E0"}
              >
                <Typography
                  color={"#F57C00"}
                  fontWeight={400}
                  fontSize={"14px"}
                  fontFamily={FontFamily.gilroy}
                  fontStyle={"italic"}
                >
                  No {language === "hindi" ? "Hindi" : "English"} translation available
                </Typography>
              </Stack>
            );
          }
          return (
            <QuestionOptionPreview
              key={option.index}
              optionText={option.text}
              optionIndex={option.index}
              language={language}
              isCorrect={option.isCorrect}
            />
          );
        })}
      </Stack>

      {/* Explanation */}
      <Stack>
        <Typography
          color={"#666"}
          fontFamily={FontFamily.gilroy}
          fontSize={"14px"}
          fontWeight={600}
          mb={"8px"}
        >
          Explanation:
        </Typography>
        {explanationText ? (
          <Stack
            p={"12px"}
            border={"1px solid #C8C8C8"}
            borderRadius={"5px"}
            bgcolor={"#FFFFFF"}
          >
            <Typography
              component={"div"}
              color={"#121212"}
              fontWeight={400}
              fontSize={"14px"}
              lineHeight={"125%"}
              fontFamily={FontFamily.gilroy}
            >
              <div
                style={{ wordBreak: "break-word", whiteSpace: "normal" }}
                dangerouslySetInnerHTML={{
                  __html: explanationText || "",
                }}
              />
            </Typography>
          </Stack>
        ) : (
          <Stack
            p={"12px"}
            border={"1px solid #FFB74D"}
            borderRadius={"5px"}
            bgcolor={"#FFF3E0"}
          >
            <Typography
              color={"#F57C00"}
              fontWeight={400}
              fontSize={"14px"}
              fontFamily={FontFamily.gilroy}
              fontStyle={"italic"}
            >
              No {language === "hindi" ? "Hindi" : "English"} translation available
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default QuestionPreviewItem;
